import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as kds from "@/lib/kdsApi";

// A ticket's age drives its colour, so the grid must re-render even when nothing
// arrives from the server. Ten seconds is finer than the minute the UI displays.
const AGE_TICK_MS = 10000;

// Realtime carries KOT fires, but a kitchen screen that silently stops updating is
// the worst failure this app has. Poll as a floor under the socket, not instead of it.
const POLL_INTERVAL_MS = 5000;

// A socket reconnect replays every event since the last epoch. One full refresh per
// replayed event meant a dozen ticket+settings reads for a single reconnect, all of
// them describing the same board. Collapse a burst into one trailing read.
const REALTIME_BURST_MS = 250;

const minutesSince = iso => {
  if (!iso) return 0;
  const started = Date.parse(iso);
  if (Number.isNaN(started)) return 0;
  return Math.max(0, Math.floor((Date.now() - started) / 60000));
};

/**
 * Live kitchen tickets from the server.
 *
 * This replaces a demo-seeded IndexedDB collection whose "bump" queued an
 * UPDATE_KOT action the sync dispatcher rejects: the card vanished locally, the
 * toast claimed success, and the kitchen's actual queue never changed.
 *
 * @param subscribeRealtime from POSContext; re-reads when any order changes.
 * @param stationId optional printer id to show only that station's items.
 */
export function useKdsTickets(subscribeRealtime, stationId = null) {
  const [tickets, setTickets] = useState([]);
  // Which station the tickets currently in hand were read for. A consumer that has
  // to tell "this is a different board" from "these are new KOTs" cannot use the
  // selected station for it: the selection changes a full render before the board
  // it asked for arrives.
  const [boardStation, setBoardStation] = useState(null);
  const [stations, setStations] = useState([]);
  const [kdsSettings, setKdsSettings] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const stationRef = useRef(stationId);
  stationRef.current = stationId;
  // The 15s poll, the realtime handler and every bump all call refresh(), so several
  // reads are routinely in flight at once and the last *response* wins rather than
  // the last request. A poll issued before a bump can land after it and put the
  // completed card back on the kitchen screen. Only the newest request may write.
  const readSeq = useRef(0);

  const refresh = useCallback(async () => {
    const seq = (readSeq.current += 1);
    const readFor = stationRef.current;
    try {
      const [rows, settingsData] = await Promise.all([
        kds.listTickets(readFor),
        kds.getKdsSettings(readFor)
      ]);
      if (readSeq.current !== seq) return;
      setTickets(rows);
      setBoardStation(readFor);
      setKdsSettings(settingsData);
      setError(null);
    } catch (failure) {
      if (readSeq.current !== seq) return;
      setError(failure?.response?.data?.detail || "Cannot reach the kitchen service");
    } finally {
      if (readSeq.current === seq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Changing station replaces the board wholesale. Flagging the read as loading
    // keeps consumers from mistaking the previous station's tickets for the new
    // station's — the last board stays on screen until the new one lands.
    setLoading(true);
    refresh();
  }, [refresh, stationId]);

  useEffect(() => {
    kds.listStations().then(setStations).catch(() => setStations([]));
  }, []);

  // Trailing throttle: the first event of a burst arms a timer, every event that
  // lands inside the window rides along, and exactly one read follows.
  const burstTimer = useRef(null);
  const refreshBurst = useCallback(() => {
    if (burstTimer.current) return;
    burstTimer.current = window.setTimeout(() => {
      burstTimer.current = null;
      refresh();
    }, REALTIME_BURST_MS);
  }, [refresh]);

  useEffect(() => {
    if (typeof subscribeRealtime !== "function") return undefined;
    return subscribeRealtime(event => {
      if (event.type === "order_update" || event.type === "resync") refreshBurst();
    });
  }, [refreshBurst, subscribeRealtime]);

  // Cancel the pending read on unmount only. Putting this in the subscription
  // effect's cleanup meant any re-render that changed the subscribe callback's
  // identity swallowed the queued refresh entirely — events in, no read out.
  useEffect(
    () => () => {
      if (burstTimer.current) {
        window.clearTimeout(burstTimer.current);
        burstTimer.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const poll = window.setInterval(refresh, POLL_INTERVAL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, [refresh]);

  const bump = useCallback(
    async ticket => {
      // 'served' is the terminal stage; the server enforces forward-only ranks.
      await kds.bumpTicket(ticket.order_id, ticket.kot_number, "served");
      await refresh();
    },
    [refresh]
  );

  // The countdown the cook watches is read from the *station's* settings, so the
  // server has to enforce that same station's recall window. Dropping the station
  // made it fall back to the global window: the button stayed lit past the real
  // deadline, or died while the station still allowed the undo.
  const recall = useCallback(
    async ticket => {
      await kds.recallTicket(ticket.order_id, ticket.kot_number, stationRef.current);
      await refresh();
    },
    [refresh]
  );

  // `now` is a dependency on purpose: it is what makes elapsed minutes advance.
  const decorated = useMemo(
    () =>
      tickets.map(ticket => ({
        ...ticket,
        key: `${ticket.order_id}-${ticket.kot_number}`,
        minutes: minutesSince(ticket.kot_sent_at),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tickets, now]
  );

  return { tickets: decorated, boardStation, stations, kdsSettings, bump, recall, refresh, error, loading };
}
